/**
 * @description Enum of all permissions in the system using backend technical names.
 * This matches the new consolidated permission structure from the backend API.
 */
export enum Permission {
  // Authentication Module
  MANAGE_INVITATION_EMAIL = "authentication.manage_invitation_email",
  MANAGE_MFA_SETTINGS = "authentication.manage_mfa_settings",
  VIEW_PASSWORD_POLICY = "authentication.view_password_policy",
  MANAGE_PASSWORD_POLICY = "authentication.manage_password_policy",

  // Settings Module
  VIEW_EMAIL_LOGS = "settings.view_email_logs",
  VIEW_EMAIL_TEMPLATES = "email_templates.view_email_templates",
  MANAGE_EMAIL_TEMPLATES = "email_templates.manage_email_templates",
  VIEW_NOTIFICATION_BINDINGS = "notification_bindings.view_notification_bindings",
  MANAGE_NOTIFICATION_BINDINGS = "notification_bindings.manage_notification_bindings",
  VIEW_TAXES = "settings.view_taxes",
  MANAGE_TAXES = "settings.manage_taxes",
  VIEW_OPERATION_LOGS = "settings.view_operation_logs",
  VIEW_ENDORSEMENT_FORMS = "products.view_endorsement_forms",
  MANAGE_ENDORSEMENT_FORMS = "products.manage_endorsement_forms",
  ARCHIVE_ENDORSEMENT_FORMS = "products.archive_endorsement_forms",
  VIEW_RISK_TYPES = "products.view_risk_types",
  MANAGE_RISK_TYPES = "products.manage_risk_types",

  // Loss run templates modele
  VIEW_LOSS_RUN_TEMPLATES = "loss_run_templates.view_loss_run_templates",
  MANAGE_LOSS_RUN_TEMPLATES = "loss_run_templates.manage_loss_run_templates",

  // Loss run imports module
  VIEW_LOSS_RUN = "loss_run_imports.view_loss_run_imports",
  MANAGE_LOSS_RUN = "loss_run_imports.manage_loss_run_imports",

  VIEW_TAGS = "settings.view_tags",
  MANAGE_TAGS = "settings.manage_tags",

  // Users and Access Module
  VIEW_USERS_AND_ACCESS = "users_and_access.view_users_and_access",
  MANAGE_USERS_AND_ACCESS = "users_and_access.manage_users_and_access",
  MANAGE_TEMPORARY_PASSWORD_FOR_USERS = "users_and_access.manage_temporary_password_for_users",
  ARCHIVE_USERS = "users_and_access.archive_users",
  UNARCHIVE_USERS = "users_and_access.unarchive_users",
  VIEW_ROLES = "users_and_access.view_roles",
  MANAGE_ROLES = "users_and_access.manage_roles",
  ARCHIVE_ROLES = "users_and_access.archive_roles",
  MANAGE_PROFILE = "users_and_access.manage_profile",

  // Products Module
  VIEW_PRODUCTS = "products.view_products",
  VIEW_ADMIN_PRODUCTS = "products.view_admin_products",
  MANAGE_ADMIN_PRODUCTS = "products.manage_admin_products",
  ARCHIVE_ADMIN_PRODUCTS = "products.archive_admin_products",
  VIEW_COVERAGE_TYPES = "products.view_coverage_types",
  MANAGE_COVERAGE_TYPES = "products.manage_coverage_types",
  VIEW_CUSTOM_FIELDS = "products.view_custom_fields",
  MANAGE_CUSTOM_FIELDS = "products.manage_custom_fields",
  ARCHIVE_CUSTOM_FIELDS = "products.archive_custom_fields",

  // Policies Module
  VIEW_POLICIES = "policies.view_policies",
  MANAGE_POLICIES = "policies.manage_policies",
  ARCHIVE_POLICY = "policies.archive_policy",
  DELETE_POLICIES = "policies.delete_policies",
  DELETE_ENDORSEMENT = "policies.delete_endorsement",

  // Clients Module
  VIEW_CLIENTS = "clients.view_clients",
  MANAGE_CLIENTS = "clients.manage_clients",
  ARCHIVE_CLIENT = "clients.archive_client",

  // Invoices Module
  VIEW_INVOICES = "invoices.view_invoices",
  MANAGE_INVOICES = "invoices.manage_invoices",

  // Templates Module (Template Center)
  VIEW_TEMPLATES = "templates.view_templates",
  MANAGE_TEMPLATES = "templates.manage_templates",
  ARCHIVE_TEMPLATES = "templates.archive_templates",

  // Approvals Module
  VIEW_APPROVAL_WORKFLOWS = "approvals.view_workflows",
  MANAGE_APPROVAL_WORKFLOWS = "approvals.manage_workflows",
  VIEW_APPROVAL_TASKS = "approvals.view_tasks",
  MANAGE_APPROVAL_TASKS = "approvals.manage_tasks",

  // Documents Module
  VIEW_DOCUMENTS = "documents.view_documents",
  MANAGE_DOCUMENTS = "documents.manage_documents",

  // Organization Entities Module
  MANAGE_ORGANIZATIONS = "organization_entities.manage_organizations",

  //TEST
  TEST = "FOR TEST",

  // General Ledger Module
  VIEW_GL_ACCOUNTS = "gl.account.read",
  MANAGE_GL_ACCOUNTS = "gl.account.write",
  ADMIN_GL_ACCOUNTS = "gl.account.admin",
  MANAGE_GL_BOOKS = "gl.book.write",
  MANAGE_GL_MAPPING_RULES = "gl.mappingrule.write",
  CREATE_GL_JOURNAL = "gl.journal.create",
  APPROVE_GL_JOURNAL = "gl.journal.approve",
  POST_GL_JOURNAL = "gl.journal.post",
  REVERSE_GL_JOURNAL = "gl.journal.reverse",
  CLOSE_GL_PERIOD = "gl.period.close",
  REOPEN_GL_PERIOD = "gl.period.reopen",
  OVERRIDE_GL_RECOGNITION = "gl.override.recognition",
  OVERRIDE_CLOSED_PERIOD = "OverrideClosedPeriodPolicy",
  VIEW_GL_REPORTS = "gl.report.read",
  ADMIN_GL_REPORTS = "gl.report.admin",
  MANAGE_AR_INVOICES = "ar.invoice.write",
  MANAGE_AR_RECEIPTS = "ar.receipt.write",
  APPROVE_AR_WRITEOFF = "ar.writeoff.approve",
  MANAGE_AP_BILLS = "ap.bill.write",
  APPROVE_AP_BILLS = "ap.bill.approve",
  MANAGE_AP_PAYMENTS = "ap.payment.write",
  VIEW_BANK_RECON = "gl.bankrecon.read",
  MATCH_BANK_RECON = "gl.bankrecon.match",
  COMPLETE_BANK_RECON = "gl.bankrecon.complete",

  // Reporting Module
  VIEW_REPORTING = "reporting.view_reports",
  MANAGE_REPORTING = "reporting.manage_reports",

  // Rating Tables Module
  VIEW_RATING_TABLES = "rating_tables.view_rating_tables",
  MANAGE_RATING_TABLES = "rating_tables.manage_rating_tables",
  PUBLISH_RATING_TABLES = "rating_tables.publish_rating_tables",
  ARCHIVE_RATING_TABLES = "rating_tables.archive_rating_tables",
  DELETE_RATING_TABLES = "rating_tables.delete_rating_tables",

  // Claims Module
  VIEW_CLAIMS = "claims.view_claims",
  CREATE_CLAIMS = "claims.create_claims",
  MANAGE_CLAIMS = "claims.manage_claims",
  VIEW_SENSITIVE_CLAIMS = "claims.view_sensitive_claims",
  VIEW_ALL_CLAIMS = "claims.view_all_claims",
  LINK_CLAIM_POLICY = "claims.link_claim_policy",
  BULK_ASSIGN_CATASTROPHE = "claims.bulk_assign_catastrophe",
  RECLASSIFY_CLAIM_COVERAGE = "claims.reclassify_coverage",
  RECORD_INVESTIGATION_SUMMARY = "claims.record_investigation_summary",
  VIEW_LOSS_RUN_REPORT = "claims.view_loss_run",
  VIEW_CLAIMS_CONFIGURATION = "claims.view_configuration",
  MANAGE_CLAIMS_CONFIGURATION = "claims.manage_configuration",
  VIEW_CLAIMS_CONFIGURATION_HISTORY = "claims.view_configuration_history",
  RECORD_CLAIM_DECISION = "claims.record_decision",
  MANAGE_CLAIM_RESERVES = "claims.manage_reserves",
  MANAGE_CLAIM_PAYMENTS = "claims.manage_payments",
  ISSUE_CLAIM_PAYMENTS = "claims.issue_payments",
  VOID_CLAIM_PAYMENTS = "claims.void_payments",
  MANAGE_LITIGATION_BUDGET = "claims.manage_litigation_budget",
  REOPEN_AGED_CLAIM = "claims.reopen_aged_claim",

  // Notification Settings Module
  VIEW_NOTIFICATION_SETTINGS = "notification_settings.view_notification_settings",
  MANAGE_NOTIFICATION_SETTINGS = "notification_settings.manage_notification_settings",

  // Quote Module
  VIEW_QUOTES = "quote.view_quotes",
  MANAGE_QUOTES = "quote.manage_quotes",
  CONVERT_QUOTES = "quote.convert_quotes",
  DELETE_QUOTES = "quote.delete_quotes",

  // Captive Management Module
  VIEW_CAPTIVE_MANAGEMENT = "captive.view",
  MANAGE_CAPTIVES = "captive.manage_captives",
  MANAGE_CELLS = "captive.manage_cells",
  MANAGE_CAPTIVE_CLIENTS = "captive.manage_clients",
}
